import { useRef, useState } from "react";
import type { ChangeReason } from "../../types";
import { CHANGE_REASONS } from "../../types";
import { Button } from "../Button";
import { ModalShell } from "../ModalShell";
import { SelectInput, TextArea } from "../fields";

export interface RatingChangeDetails {
  notes: string | null;
  changeReason: ChangeReason | null;
  changeExplanation: string | null;
}

interface RatingChangeModalProps {
  title: string;
  description: string;
  /** Debit overrides require a note (product plan 4.8). */
  requireNote: boolean;
  /** AI-V2 groups require a Change Reason (product plan 4.10). */
  requireChangeReason: boolean;
  initialNotes?: string | null;
  confirmLabel: string;
  confirmVariant?: "primary" | "danger";
  onConfirm: (details: RatingChangeDetails) => Promise<void>;
  onCancel: () => void;
  children?: never;
}

const REASON_OPTIONS = [{ value: "", label: "Select a reason" }, ...CHANGE_REASONS.map((value) => ({ value, label: value }))];

/** Collects the note and, on AI-V2 groups, the Change Reason before a rating edit, add, or removal proceeds. Cancel aborts the change. */
export function RatingChangeModal({ title, description, requireNote, requireChangeReason, initialNotes, confirmLabel, confirmVariant = "primary", onConfirm, onCancel }: RatingChangeModalProps) {
  const [notes, setNotes] = useState(initialNotes ?? "");
  const [changeReason, setChangeReason] = useState<ChangeReason | "">("");
  const [changeExplanation, setChangeExplanation] = useState("");
  const [errors, setErrors] = useState<{ notes?: string; changeReason?: string; changeExplanation?: string }>({});
  const [isSaving, setIsSaving] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const handleConfirm = async () => {
    const nextErrors: typeof errors = {};
    if (requireNote && notes.trim() === "") nextErrors.notes = "A note is required when overriding debits.";
    if (requireChangeReason && changeReason === "") nextErrors.changeReason = "Change Reason is required on AI-V2 groups.";
    if (changeReason === "Skynet Error" && changeExplanation.trim() === "") nextErrors.changeExplanation = "Explain the Skynet error.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setIsSaving(true);
    try {
      await onConfirm({ notes: notes.trim() || null, changeReason: changeReason === "" ? null : changeReason, changeExplanation: changeExplanation.trim() || null });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalShell
      title={title}
      description={description}
      onClose={onCancel}
      initialFocusRef={cancelRef}
      size="sm"
      footer={
        <>
          <Button ref={cancelRef} variant="secondary" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant={confirmVariant} onClick={() => void handleConfirm()} isLoading={isSaving}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {requireChangeReason && <SelectInput id="change-reason" label="Change Reason" value={changeReason} onChange={(value) => setChangeReason(value as ChangeReason | "")} options={REASON_OPTIONS} error={errors.changeReason} required />}
        {requireChangeReason && <TextArea id="change-explanation" label="Change Explanation" value={changeExplanation} onChange={setChangeExplanation} error={errors.changeExplanation} required={changeReason === "Skynet Error"} hint="Required when the reason is Skynet Error." />}
        <TextArea id="change-notes" label="Notes" value={notes} onChange={setNotes} error={errors.notes} required={requireNote} />
      </div>
    </ModalShell>
  );
}
